import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { Change_Supplier_BusinessStatus } from "apollo/mutuations";
import { GET_SUPPLIERS_Business } from "apollo/queries";
import AdminUpdateSupplierBusiness from "components/SupplierBusiness/AdminUpdateSupplierBussiness";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import SupplierDetailModal from "./detail";
import SupplierFilter from "./supplierFilter";

const PendingSupplierBusiness = ({ activeTab }) => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedStatusChange, setStatusChange] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [status, setStatus] = useState(null);
  const [total, setTotal] = useState(0);
  const [supplier, setSupplier] = useState(null);

  const { data, loading, refetch } = useQuery(GET_SUPPLIERS_Business, {
    variables: {
      first: parseInt(size),
      page: activePage,
      status: "PENDING",
      supplier_id: supplier,
      is_sme: false,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.suppliersBusinesses.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    if (activeTab === "second") refetch();
  }, [activeTab]);

  const theme = useMantineTheme();
  const handleManageBusiness = (id) => {
    setEditId(id);
    setOpened(true);
  };
  const headerData = [
    {
      label: "Name",
      key: "business_name",
      sortable: true,
      searchable: true,
      width: "20%",
      render: (rowData) => {
        return <span>{rowData.business_name}</span>;
      },
    },
    {
      label: "Phone",
      key: "business_phone_number",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.business_phone_number}</span>;
      },
    },
    {
      label: "Email",
      key: "business_email",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.business_email}</span>;
      },
    },
    {
      label: "Warehouse",
      key: "number_of_warehouses",
      sortable: false,
      width: "8%",
      searchable: true,
      render: (rowData) => {
        return (
          <span style={{ display: "block", width: "30px" }}>
            {rowData.number_of_warehouses}
          </span>
        );
      },
    },
    {
      label: "Tin",
      key: "tin",
      sortable: false,
      width: "10%",
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.tin}</span>;
      },
    },
    {
      label: "Status",
      key: "account",
      width: "8%",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.status === "ACTIVE" ? (
              <Badge variant="light" color="green">
                Active
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Not Active
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <ActionButton label="Edit" onClick={() => handleEditUser(rowData)}>
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleManageBusiness(rowData)}
            >
              <FiEye style={{ fontSize: "1rem" }} />
            </ActionButton>

            <ActionButton
              label={rowData.status === "ACTIVE" ? "Deactivate" : "Activate"}
              color={rowData.status === "ACTIVE" ? "deactivate" : "activate"}
              onClick={() =>
                handleStatus(
                  rowData.id,
                  rowData.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE",
                )
              }
            >
              {rowData.status === "ACTIVE" ? (
                <PersonOffIcon size={17} />
              ) : (
                <HowToRegIcon size={17} />
              )}
            </ActionButton>
          </div>
        );
      },
    },
  ];
  const [changeUserStatus] = useMutation(Change_Supplier_BusinessStatus, {
    refetchQueries: [
      {
        query: GET_SUPPLIERS_Business,
        variables: {
          first: parseInt(size),
          page: activePage,
          status: "PEMDING",
          supplier_id: supplier,
          search: searchValue,
          ordered_by: [
            {
              column: "CREATED_AT",
              order: "DESC",
            },
          ],
        },
      },
    ],
    onCompleted(data) {
      const action =
        data.changeSupplierBusinessStatus.status === "ACTIVE"
          ? "Activated"
          : "Deactivated";
      showNotification({
        color: "green",
        title: "Success",
        message: `Supplier Business ${action} successfully`,
      });
      setStatusChange(false);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: `${error.message}`,
      });
    },
  });

  const handleUserStatusChange = () => {
    changeUserStatus({
      variables: {
        id: deleteID, // Ensure id is an integer
        status: status, // Toggle the status
      },
    });
  };

  const handleStatus = (id, status) => {
    setStatusChange(true);
    setDeleteID(id);
    setStatus(status);
  };

  const handleEditUser = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        padding="xl"
        title="Update Supplier Business"
        size="80%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        onClose={() => setOpenedEdit(false)}
      >
        <AdminUpdateSupplierBusiness setOpened={setOpenedEdit} data={editId} />
      </Drawer>
      <Modal
        opened={openedStatusChange}
        onClose={() => setStatusChange(false)}
        title="Warning"
        centered
      >
        <p>{`Are you sure do you want to ${
          status === "ACTIVE" ? "Activat" : "Deactivat"
        } this  user?`}</p>
        <Group position="right">
          <Button onClick={() => handleUserStatusChange()} color="red">
            {status === "ACTIVE" ? "Activat" : "Deactivat"}
          </Button>
        </Group>
      </Modal>
      <Drawer
        opened={opened}
        padding="xl"
        title="Detail Supplier Busines"
        size="80%"
        position="bottom"
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        onClose={() => setOpened(false)}
      >
        <SupplierDetailModal setOpened={setOpened} data={editId} />
      </Drawer>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data.suppliersBusinesses.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            loading={loading}
            layout={{ minWidth: 700 }}
            data={data ? data.suppliersBusinesses.data : []}
            size={size}
            filterData={({ onCardClick }) => (
              <SupplierFilter supplier={supplier} onCardClick={setSupplier} />
            )}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default PendingSupplierBusiness;
